/**
 * useNovelChapterContent - Fetch novel chapter content for reader
 * Location: /app/frontend/src/components/page-builder/reader/hooks/
 */

import { useState, useEffect } from 'react';
import { apiClient } from '../../../../config/api';

/**
 * Hook to fetch novel chapter content (text)
 * @param {string} novelId - The novel ID
 * @param {string} chapterId - The chapter ID
 * @param {object} options - Optional config { version: 'draft' | 'published' }
 */
export const useNovelChapterContent = (novelId, chapterId, options = {}) => {
  const [content, setContent] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!novelId || !chapterId) {
      setLoading(false);
      setError('Missing novel or chapter ID');
      return;
    }

    const fetchContent = async () => {
      setLoading(true);
      setError(null);

      try {
        // Try the read endpoint first
        const versionParam = options.version === 'draft' ? '?version=draft' : '';
        const response = await apiClient.get(
          `/novels/${novelId}/chapters/${chapterId}/read${versionParam}`
        );

        const data = response.data;
        
        // Normalize content structure
        const textContent = data.content || data.text || data.body || '';
        // Check if content contains HTML tags
        const hasHtmlTags = /<[a-z][\s\S]*>/i.test(textContent);
        
        setContent({
          text: textContent,
          contentHtml: hasHtmlTags ? textContent : null, // Set contentHtml if HTML detected
          type: 'novel',
          wordCount: data.word_count || textContent.split(/\s+/).length,
          // For novels, we can paginate by paragraphs or word count if needed
          paragraphs: textContent.split('\n\n').filter(p => p.trim()),
        });

        setMetadata({
          id: data.id || chapterId,
          title: data.title || 'Chapter',
          novelId: novelId,
          novelTitle: data.novel_title || data.novelTitle || '',
          chapterNumber: data.chapter_number || data.order || 1,
          nextChapterId: data.next_chapter_id || null,
          prevChapterId: data.prev_chapter_id || null,
          createdAt: data.created_at,
          publishedAt: data.published_at,
        });

      } catch (err) {
        console.error('[useNovelChapterContent] Error:', err);
        
        // Fallback: try to get chapter data directly
        try {
          const fallbackResponse = await apiClient.get(`/novel-chapters/${chapterId}`);
          const fallbackData = fallbackResponse.data;
          
          setContent({
            text: fallbackData.content || fallbackData.text || '',
            type: 'novel',
            wordCount: (fallbackData.content || '').split(/\s+/).length,
            paragraphs: (fallbackData.content || '').split('\n\n').filter(p => p.trim()),
          });

          setMetadata({
            id: fallbackData.id || chapterId,
            title: fallbackData.title || 'Chapter',
            novelId: novelId,
            novelTitle: fallbackData.novel_title || '',
            chapterNumber: fallbackData.chapter_number || 1,
          });

        } catch (fallbackErr) {
          setError(err.response?.data?.detail || err.message || 'Failed to load chapter');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [novelId, chapterId, options.version]);

  return { content, metadata, loading, error, refetch: () => setLoading(true) };
};

export default useNovelChapterContent;
